import { Warnings } from '../components/SidebarElementPropertyRule';
import { AvailableRawValues, ElementPropertyRuleConfig } from '../types';
import { Type } from '@fullstory/proto/fs/api/vars';
import { assertExhaustive } from '@fullstory/typeutils';
import { useEffect, useMemo, useState } from 'react';
import { intl } from '@fullstory/intl';
import { uniq } from 'lodash';
import { ObjectCollection } from '@fullstory/renderer-shared';
import {
  flattenSampleValues,
  getSampleValues,
  rawTransformedValues,
  rawValueAsDate,
} from '../utils/property-rules';

interface Props {
  elementPropertyRule: ElementPropertyRuleConfig;
  availableValues: AvailableRawValues | undefined;
  highlightedObjects: ObjectCollection;
  isPrivacyRuleSelector?: boolean;
}

export const useElementPropertyRulesWarnings = ({
  elementPropertyRule,
  availableValues,
  highlightedObjects,
  isPrivacyRuleSelector,
}: Props): Warnings => {
  const groupedSampleValues = useMemo(
    () => getSampleValues(elementPropertyRule, availableValues),
    [elementPropertyRule, availableValues],
  );
  const sampleValues = useMemo(
    () => flattenSampleValues(groupedSampleValues),
    [groupedSampleValues],
  );
  const groupedSampleTransformedValues = useMemo(
    () => rawTransformedValues(elementPropertyRule.regex, groupedSampleValues),
    [elementPropertyRule.regex, groupedSampleValues],
  );
  const sampleTransformedValues = useMemo(
    () => flattenSampleValues(groupedSampleTransformedValues),
    [groupedSampleTransformedValues],
  );

  const { hasContainer, hasValueSource } = useMemo(() => {
    const res = { hasContainer: false, hasValueSource: false };
    Object.values(highlightedObjects).forEach(obj => {
      if (obj.groups.includes('inspected')) {
        res.hasContainer = true;
      }
      if (obj.groups.includes('inspected-context')) {
        res.hasValueSource = true;
      }
    });
    return res;
  }, [highlightedObjects]);

  const elementWarnings = useMemo(
    (): string[] =>
      hasValueSource
        ? []
        : [
            intl.formatMessage({
              defaultMessage: 'No matches found for the element in the preview.',
              id: '5fVS0f',
            }),
          ],
    [hasValueSource],
  );

  const elementPrivacyRuleWarnings = useMemo(
    (): string[] =>
      isPrivacyRuleSelector
        ? [
            intl.formatMessage({
              defaultMessage:
                'Warning: Unable to extract values from masked or partially masked text. Please review privacy rules in Settings.',
              id: 'GGu3H6',
            }),
          ]
        : [],
    [isPrivacyRuleSelector],
  );

  // Note: Parent warnings are only shown when there is an actual parent element enabled.
  //       In the case that the parent element is not enabled, we only show the element warnings above.
  const parentWarnings = useMemo(
    (): string[] =>
      hasContainer
        ? []
        : [
            intl.formatMessage({
              defaultMessage: 'No matches found for the parent element in the preview.',
              id: 'Ni/d4L',
            }),
          ],
    [hasContainer],
  );

  const valueWarnings = useMemo((): string[] => {
    const warnings: string[] = [];
    const ignoreEmpty =
      !hasValueSource ||
      !!elementPropertyRule?.static ||
      elementPropertyRule?.propertyType === Type.bool ||
      elementPropertyRule?.propertyType === Type.bools;
    if (!ignoreEmpty) {
      if (!sampleValues?.length) {
        warnings.push(
          intl.formatMessage({
            defaultMessage: 'No values found for this selection.',
            id: '1CkeZ3',
          }),
        );
      }
      if (sampleValues?.includes('')) {
        warnings.push(
          intl.formatMessage({
            defaultMessage: 'At least one empty value found for this selection.',
            id: 'twWu6k',
          }),
        );
      }
    }
    return uniq(warnings);
  }, [
    hasValueSource,
    elementPropertyRule?.static,
    elementPropertyRule?.propertyType,
    sampleValues,
  ]);

  const regexWarnings = useMemo((): string[] => {
    const warnings: string[] = [];
    if (sampleTransformedValues?.includes(undefined)) {
      warnings.push(
        intl.formatMessage({
          defaultMessage: 'At least one value does not match the configured regex.',
          id: 's8HIgw',
        }),
      );
    }
    return warnings;
  }, [sampleTransformedValues]);

  const propertyTypeWarnings = useMemo((): string[] => {
    const warnings: string[] = [];
    sampleTransformedValues?.forEach(value => {
      if (value !== undefined) {
        warnings.push(...warningsForPropertyType(value, elementPropertyRule.propertyType));
      }
    });
    const selectedPropertyTypeIsNumeric = [Type.real, Type.reals, Type.int, Type.ints].includes(
      elementPropertyRule.propertyType,
    );
    if (
      elementPropertyRule.propertyType &&
      !selectedPropertyTypeIsNumeric &&
      sampleTransformedValues?.length
    ) {
      // let's also warn if all of the output sample values appear numeric but a numeric type is not selected
      const allValuesNumeric = sampleTransformedValues.every(
        value => value !== undefined && !Number.isNaN(Number(value)),
      );
      if (allValuesNumeric) {
        warnings.push(
          intl.formatMessage({
            defaultMessage: 'All values appear to be numeric, but a non-numeric type is selected.',
            id: 'dB+6FF',
          }),
        );
      }
    }
    return uniq(warnings);
  }, [sampleTransformedValues, elementPropertyRule.propertyType]);

  const warnings: Warnings = useMemo(() => {
    const hasNonEmptyContainer =
      elementPropertyRule.any ||
      !!elementPropertyRule.namedElementId ||
      elementPropertyRule.selectors?.selectors?.some(sel => !!sel.selector);
    const hasChildElement =
      elementPropertyRule.anyChild ||
      elementPropertyRule.childNamedElementId !== undefined ||
      !!elementPropertyRule.childSelectors;
    const hasNonEmptyChild =
      elementPropertyRule.anyChild ||
      !!elementPropertyRule.childNamedElementId ||
      elementPropertyRule.childSelectors?.selectors?.some(sel => !!sel.selector);
    const hasNonEmptyValue =
      !!elementPropertyRule.attribute?.attribute ||
      !!elementPropertyRule.innerText ||
      !!elementPropertyRule.static?.value;
    return {
      element:
        (hasChildElement && hasNonEmptyChild) || (!hasChildElement && hasNonEmptyContainer)
          ? elementWarnings
          : undefined,
      parent: hasChildElement && hasNonEmptyContainer ? parentWarnings : undefined,
      value: hasNonEmptyValue ? valueWarnings : undefined,
      regex: hasNonEmptyValue ? regexWarnings : undefined,
      propertyName: undefined, // don't currently have any here
      propertyType: propertyTypeWarnings,
      privacy: elementPrivacyRuleWarnings,
    };
  }, [
    elementPropertyRule,
    elementWarnings,
    parentWarnings,
    valueWarnings,
    regexWarnings,
    propertyTypeWarnings,
    elementPrivacyRuleWarnings,
  ]);

  // given the warnings are largely based on the preview and preview feedback
  // lets give any changes a moment to settle before showing the warnings to avoid flickering
  // starting simple with a debounce -- may want to hook into the preview highlighting lifecycle at some point for more direct state
  // additionally, as soon as any warnings are empty, we respect those immediately to prefer not showing warnings
  const [debouncedWarnings, setDebouncedWarnings] = useState<Warnings>(warnings);

  useEffect(() => {
    // set any empty values immediately
    setDebouncedWarnings(existing => ({
      element: !warnings.element?.length ? undefined : existing.element,
      parent: !warnings.parent?.length ? undefined : existing.parent,
      value: !warnings.value?.length ? undefined : existing.value,
      propertyName: !warnings.propertyName?.length ? undefined : existing.propertyName,
      propertyType: !warnings.propertyType?.length ? undefined : existing.propertyType,
      privacy: !warnings.privacy?.length ? undefined : existing.privacy,
    }));
    // then set the full warnings value on a small timeout
    const handler = setTimeout(() => {
      setDebouncedWarnings(warnings);
    }, 250);
    return () => {
      clearTimeout(handler);
    };
  }, [warnings]);

  return debouncedWarnings;
};

function warningsForPropertyType(value: string, propertyType: Type): string[] {
  const strictNumber = (value: string) => {
    if (value.trim() != value) return Number.NaN;
    return Number(value);
  };

  switch (propertyType) {
    case Type.bool:
    case Type.bools:
      // all bool values are valid
      // when not a strict bool value, we just parse based on existence of a value
      return [];
    case Type.date:
    case Type.dates: {
      // dates can be one of a couple formats
      const date = rawValueAsDate(value);
      if (!date) {
        return [
          intl.formatMessage({
            defaultMessage: "At least one value cannot be parsed as a 'date'.",
            id: 'b9vHC3',
          }),
        ];
      }
      return [];
    }
    case Type.int:
    case Type.ints: {
      const asNumber = strictNumber(value);
      if (Number.isNaN(asNumber) || !Number.isInteger(asNumber)) {
        return [
          intl.formatMessage({
            defaultMessage: "At least one value cannot be parsed as an 'int'.",
            id: 'WKqvOD',
          }),
        ];
      }
      return [];
    }
    case Type.real:
    case Type.reals: {
      const asNumber = strictNumber(value);
      if (Number.isNaN(asNumber)) {
        return [
          intl.formatMessage({
            defaultMessage: "At least one value cannot be parsed as a 'real'.",
            id: 'fMbhGT',
          }),
        ];
      }
      return [];
    }
    case Type.str:
    case Type.strs:
      // all string values are valid
      return [];

    case Type.INVALID_TYPE:
      // unset not used
      return [];

    default:
      assertExhaustive(propertyType);
  }
}
